// Estadísticas para el asistente con IA (plan Pro). El panel ya puede leer los docs
// stats/{usuarioEmpresa}_{fecha} del dueño, así que acá se cargan los últimos dos meses, se
// resumen con las mismas cuentas de la sección Estadísticas (statsData.js) y viajan a n8n
// junto con el mensaje. La IA solo las lee para responder: no cambia nada.
import { DETAILED_STATS_SINCE, dayKey, loadStats } from "./stats.js";
import { WEEKDAYS, hourRange, peaks, summarize } from "./components/panel/statsData.js";

// Puestos de cada ranking que se mandan.
export const TOP = 5;
// Días que van uno por uno (para "¿cuántas visitas tuve el martes?").
export const DAILY = 14;
// Cada cuánto se vuelven a leer de Firestore mientras el panel está abierto.
export const STATS_TTL_MS = 5 * 60 * 1000;

const round = (n, d = 0) => Math.round(n * 10 ** d) / 10 ** d;
const pct = (part, whole) => whole > 0 ? round(part / whole * 100, 1) : 0;
const top = (list) => list.slice(0, TOP).map((r) => `${r.product.name} (${r.n})`);

// Días a cargar: los últimos 60 o desde el 1 del mes pasado, lo que sea más.
export const daysNeeded = (today = new Date()) => {
    const prevStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.max(60, Math.round((start - prevStart) / 86400000) + 1);
}

// Resumen de un período, con nombres en español para que la IA lo entienda solo.
export const periodSummary = (days, products) => {
    if (!days.length) return null;
    const s = summarize(days, products);
    const t = s.totals;
    const visits = peaks(s.heat.visitas);
    const orders = peaks(s.heat.pedidos);
    const best = days.reduce((a, d) => d.visitas > a.visitas ? d : a, days[0]);
    const out = {
        desde: dayKey(days[0].date),
        hasta: dayKey(days[days.length - 1].date),
        visitas: t.visitas,
        visitasDesdeQr: t.visitasQr,
        visitasDesdeLink: Math.max(0, t.visitas - t.visitasQr),
        abrieronAlgunProducto: t.exploraron,
        armaronCarrito: t.carritos,
        pedidosPorWhatsapp: t.pedidos,
        conversionVisitaAPedido: pct(t.pedidos, t.visitas) + "%",
        carritosSinEnviar: s.abandoned,
        importePedidos: round(t.importe),
        ticketPromedio: round(s.ticket),
        pedidosRetiro: t.retiro,
        pedidosEnvio: t.envio,
        toquesBotonWhatsapp: t.clicsWhatsapp
    };
    if (t.pedidos > t.pedidosConTotal) out.pedidosSinTotal = t.pedidos - t.pedidosConTotal;
    if (s.topViewed.length) out.masVistos = top(s.topViewed);
    if (s.topAdded.length) out.masAgregadosAlCarrito = top(s.topAdded);
    if (s.topSent.length) out.masPedidosEnUnidades = top(s.topSent);
    if (s.categories.length) out.categoriasMasVistas = s.categories.slice(0, TOP).map((c) => `${c.name} (${c.n})`);
    if (s.lowAdd.length) out.muyVistosPeroPocoPedidos = s.lowAdd.map((r) => `${r.product.name} (${r.n} vistas, ${r.added} agregados)`);
    if (visits.topHour >= 0) out.horaConMasVisitas = hourRange(visits.topHour);
    if (visits.topDay >= 0 && days.length >= 7) out.diaDeLaSemanaConMasVisitas = WEEKDAYS[visits.topDay];
    if (orders.topHour >= 0) out.horaConMasPedidos = hourRange(orders.topHour);
    if (orders.topDay >= 0 && days.length >= 7) out.diaDeLaSemanaConMasPedidos = WEEKDAYS[orders.topDay];
    if (days.length > 1 && best.visitas > 0) out.diaConMasVisitas = `${dayKey(best.date)} (${WEEKDAYS[(best.date.getDay() + 6) % 7]}, ${best.visitas} visitas)`;
    return out;
}

// Todo lo que viaja a n8n. `days` es lo que devuelve loadStats: del más viejo al de hoy.
export const buildStatsContext = (days, products, today = new Date()) => {
    const last = (n, offset = 0) => days.slice(Math.max(0, days.length - n - offset), days.length - offset);
    const month = today.getMonth();
    const thisMonth = days.filter((d) => d.date.getMonth() === month && d.date.getFullYear() === today.getFullYear());
    const prev = new Date(today.getFullYear(), month - 1, 1);
    const prevMonth = days.filter((d) => d.date.getMonth() === prev.getMonth() && d.date.getFullYear() === prev.getFullYear());
    const periods = [
        ["hoy", last(1)],
        ["ayer", last(1, 1)],
        ["ultimos7Dias", last(7)],
        ["los7DiasAnteriores", last(7, 7)],
        ["ultimos30Dias", last(30)],
        ["los30DiasAnteriores", last(30, 30)],
        ["esteMes", thisMonth],
        ["mesPasado", prevMonth]
    ];
    return {
        hoy: dayKey(today),
        medicionDetalladaDesde: DETAILED_STATS_SINCE,
        periodos: Object.fromEntries(periods.map(([name, list]) => [name, periodSummary(list, products)]).filter(([, v]) => v)),
        porDia: last(DAILY).map((d) => `${dayKey(d.date)} ${WEEKDAYS[(d.date.getDay() + 6) % 7]}: ${d.visitas} visitas (${d.visitasQr} QR), ${d.pedidos} pedidos`)
    };
}

// Carga y resume. null si no se pudo leer: el asistente sigue andando sin estadísticas.
export const loadStatsContext = async (usuarioEmpresa, products) => {
    try {
        const days = await loadStats(usuarioEmpresa, daysNeeded());
        return buildStatsContext(days, products);
    } catch (error) {
        return null;
    }
}
